<template>
    <div :class="['promo-content', [currentAnimationState === 'default' ? '' : 'promo-content--center']]" ref="wrapper">
        <div
            v-for="(card, index) in ticketCards"
            :key="index"
            :id="`promo-content__card--index-${index}`"
            :class="[
                'promo-content__card',
                [currentAnimationState === 'default' ? 'promo-content__card--left' : 'promo-content__card--center'],
            ]"
        >
            <div v-if="currentAnimationState === 'default'" class="promo-content__overlay"></div>
            <TicketPromoCard :card="card" class="promo-content__inner-card" />
        </div>

        <div v-if="currentAnimationState === 'drawResult'" class="promo-content__gradient--radial"></div>
        <div v-if="currentAnimationState !== 'default'" class="promo-content__card-shadow"></div>
        <div v-if="currentAnimationState !== 'default'" class="promo-content__gradient--left"></div>
        <div v-if="currentAnimationState !== 'default'" class="promo-content__gradient--right"></div>

        <div class="promo-content__cta">
            <DsButton
                v-if="currentAnimationState === 'drawResult'"
                type="primary"
                size="sm"
                :href="ticketCards[0].pageUrl.relative"
                class="promo-content__button-buy"
                :class="{ 'game-launcher': ticketCards[0].isOnline }"
                :data-game-id="ticketCards[0].externalSystemID"
                :data-game-code="ticketCards[0].codeName"
                ref="buttonBuy"
                @click="handleScratchTicketOnline(ticketCards[0].codeName)"
                >{{ BUTTON_SCRATCH_TEXT }}
            </DsButton>

            <DsButton
                v-if="currentAnimationState === 'drawResult'"
                type="interaction"
                size="sm"
                icon-left="rotate-thin"
                @click="handleReshuffle"
                class="promo-content__button-reshuffle"
                ref="buttonShuffle"
                >{{ BUTTON_RESHUFFLE_TEXT }}
            </DsButton>
        </div>
    </div>

    <DsButton
        v-if="currentAnimationState === 'default'"
        type="primary"
        theme="dark"
        size="sm"
        icon-left="heart-thin"
        @click="handleShuffle"
        class="promo-content__button-shuffle"
        >{{ BUTTON_SHUFFLE_TEXT }}
    </DsButton>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { debounce } from "@sazka/utils/debounce";
import { useGsap } from "@sazka/utils/gsap-register";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import TicketPromoCard from "@/tickets/components/ticket-promo-card.vue";
import { checkedFilters, selectedSorting } from "@/tickets/components/tickets-filter/tickets-filter-state";
import { TicketPromoCard as TicketPromoCardModel } from "@/tickets/models/ticket-promo-card";
import { OnlineTicketsCategoryPageAnalytics } from "@/tickets/utils/online-tickets-analytics-category-page";
import breakpoints = ResponsiveUtils.breakpoints;

// CONSTANTS
const BUTTON_SHUFFLE_TEXT = "ZKUSTE ŠTĚSTÍ";
const BUTTON_SCRATCH_TEXT = "SETŘÍT ONLINE";
const BUTTON_RESHUFFLE_TEXT = "JINÝ LOS";

const DEFAULT_ANIMATION_DURATION = 24;
const DEFAULT_ANIMATION_CARD_SCALE = 0.8;
const DEFAULT_ANIMATION_ROTATION = 40;
const DRAW_ANIMATION_DURATION = 4;
const DRAW_ANIMATION_ROTATION_ANGLE = 1080;
const DRAW_ANIMATION_MAX_CARDS = 10;

const WIDGET_TARGET_ROW_PLACEMENT_MOBILE = 7;

// VARIABLES
const { gsap } = useGsap();
let ctx: gsap.Context;
let animations: gsap.core.Timeline[] = [];
let allTicketCards: TicketPromoCardModel[];
let lastWindowWidth = window.innerWidth;

const widget: HTMLElement = document.getElementById("random-recommendation-card");
const cardList: HTMLElement = document.getElementById("card-list");
const widgetsGrid: HTMLElement = document.getElementById("cp-widgets-section");

// REFS
const wrapper = ref<HTMLElement>();
const buttonBuy = ref<HTMLElement>();
const currentAnimationState = ref<"default" | "draw" | "drawResult">("default");
const ticketCards = ref<TicketPromoCardModel[]>([]);
const defaultAnimationCardsVisibleCount = ref(8);
const isDesktop = useBreakpoints("lg");

const isMobile = computed(() => {
    return !useBreakpoints("xs2").value;
});

const isWidgetInMobileLayout = computed(() => {
    return widget && cardList && widgetsGrid && isMobile.value;
});

let wasWidgetInMobileLayout: boolean;

// FUNCTIONS
function handleShuffle() {
    currentAnimationState.value = "draw";

    OnlineTicketsCategoryPageAnalytics.pushRandomBetEvent();
}

function handleScratchTicketOnline(name: string) {
    OnlineTicketsCategoryPageAnalytics.pushShowProductEvent(name, BUTTON_SCRATCH_TEXT);
}

function handleReshuffle() {
    currentAnimationState.value = "draw";

    OnlineTicketsCategoryPageAnalytics.pushRandomBetEventAgain();
}

// Calculates delay before repeating the animation based on the number of cards and a constant count.
function repeatDelay() {
    if (ticketCards.value.length < defaultAnimationCardsVisibleCount.value) {
        return 0;
    } else {
        return (
            (DEFAULT_ANIMATION_DURATION / defaultAnimationCardsVisibleCount.value) *
            (ticketCards.value.length - defaultAnimationCardsVisibleCount.value)
        );
    }
}

// Determines the time each card animation should seek to start at, for staggering effect.
function seekTime() {
    if (ticketCards.value.length < defaultAnimationCardsVisibleCount.value) {
        return DEFAULT_ANIMATION_DURATION / ticketCards.value.length;
    } else {
        return DEFAULT_ANIMATION_DURATION / defaultAnimationCardsVisibleCount.value;
    }
}

// Randomly selects and shuffles up to DRAW_ANIMATION_MAX_CARDS elements from allTicketCards
function selectAndShuffleCards(cards: TicketPromoCardModel[]): TicketPromoCardModel[] {
    const shuffled = cards.slice();
    const count = Math.min(cards.length, DRAW_ANIMATION_MAX_CARDS);
    let temp: TicketPromoCardModel;
    let i: number;
    let j: number;

    // Shuffle the copied array
    for (i = shuffled.length - 1; i > 0; i--) {
        j = Math.floor(Math.random() * (i + 1));
        temp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = temp;
    }

    // Select up to DRAW_ANIMATION_MAX_CARDS from the shuffled array
    return shuffled.slice(0, count);
}

function defaultAnimation() {
    const wrapperWidth = wrapper.value.clientWidth;

    // Adjust total count of visible cards based on widget width and screen width
    if (wrapperWidth < 255 || window.innerWidth > breakpoints.lg) {
        defaultAnimationCardsVisibleCount.value = 7;
    } else if (wrapperWidth < 300) {
        defaultAnimationCardsVisibleCount.value = 8;
    } else if (wrapperWidth < 350) {
        defaultAnimationCardsVisibleCount.value = 9;
    } else if (wrapperWidth < 390) {
        defaultAnimationCardsVisibleCount.value = 10;
    } else if (wrapperWidth < 440) {
        defaultAnimationCardsVisibleCount.value = 11;
    } else if (wrapperWidth < 483) {
        defaultAnimationCardsVisibleCount.value = 12;
    } else {
        defaultAnimationCardsVisibleCount.value = 13;
    }

    const cards = document.querySelectorAll<HTMLDivElement>(".promo-content__card");
    const cardWidth = cards[0]?.clientWidth || 0;

    cards.forEach((_, index) => {
        const currentCard = `#promo-content__card--index-${index}`;

        const CARD_TL = gsap
            .timeline({
                repeat: -1,
                repeatDelay: repeatDelay(),
            })
            .set(currentCard, {
                x: wrapperWidth / 2 + cardWidth / 2,
                rotateY: DEFAULT_ANIMATION_ROTATION,
                opacity: 0,
                scale: DEFAULT_ANIMATION_CARD_SCALE,
                display: "block",
            })
            .to(currentCard, { opacity: 1, duration: 0.1 * DEFAULT_ANIMATION_DURATION }, 0)
            .to(
                currentCard,
                { opacity: 0, duration: 0.1 * DEFAULT_ANIMATION_DURATION },
                0.9 * DEFAULT_ANIMATION_DURATION
            )
            .fromTo(
                currentCard,
                { x: -wrapperWidth / 2 - cardWidth / 2 },
                {
                    x: wrapperWidth / 2 + cardWidth / 2,
                    duration: DEFAULT_ANIMATION_DURATION,
                    immediateRender: false,
                    ease: "power1.inOut",
                },
                0
            )
            .fromTo(
                currentCard,
                { rotateY: DEFAULT_ANIMATION_ROTATION },
                {
                    rotateY: -DEFAULT_ANIMATION_ROTATION,
                    immediateRender: false,
                    duration: DEFAULT_ANIMATION_DURATION,
                    ease: "power2.inOut",
                },
                0
            )
            .to(
                currentCard,
                { z: 100, scale: 0.9, duration: 0.1 * DEFAULT_ANIMATION_DURATION, repeat: 1, yoyo: true },
                0.4 * DEFAULT_ANIMATION_DURATION
            )
            .fromTo(
                currentCard,
                { zIndex: 1 },
                {
                    zIndex: cards.length,
                    repeat: 1,
                    yoyo: true,
                    ease: "none",
                    duration: 0.5 * DEFAULT_ANIMATION_DURATION,
                    immediateRender: false,
                },
                0
            )
            .set(currentCard, {
                display: "none",
            })
            .seek(index * seekTime());

        animations.push(CARD_TL);
    });

    const tl = gsap.timeline({
        repeat: -1,
    });

    tl.to(".promo-content__button-shuffle", {
        keyframes: {
            scale: [1, 1.1, 1],
        },
        duration: 2,
    });

    animations.push(tl);
}

function drawAnimation() {
    const offsetAngle = 360 / ticketCards.value.length;
    const cards = document.querySelectorAll<HTMLDivElement>(".promo-content__card");
    const cardWidth = cards[0]?.clientWidth || 0;
    const cardHeight = cards[0]?.clientHeight || 0;
    const rotationCenter = isDesktop ? 350 : 275;

    const wrapperWidth = wrapper.value.clientWidth;
    const centerX = cardWidth / 2;
    const centerY = cardHeight / 2 + rotationCenter;

    ticketCards.value.forEach((_, index) => {
        const cardClass = `#promo-content__card--index-${index}`;
        const initialRotation = index * offsetAngle;

        const tl = gsap.timeline({ defaults: { ease: "expo.out", duration: DRAW_ANIMATION_DURATION } });

        tl.set(cardClass, {
            transformOrigin: `${centerX}px ${centerY}px`,
        });
        tl.set(".promo-content__gradient--left", {
            width: wrapperWidth / 2 - cardWidth / 2,
        });
        tl.set(".promo-content__gradient--right", {
            width: wrapperWidth / 2 - cardWidth / 2,
        });

        tl.fromTo(
            cardClass,
            {
                rotation: initialRotation,
                zIndex: 1,
            },
            {
                rotation: initialRotation + DRAW_ANIMATION_ROTATION_ANGLE,
                zIndex: 1,
                onComplete: () => {
                    if (index === ticketCards.value.length - 1) {
                        currentAnimationState.value = "drawResult";
                    }
                },
            }
        ).seek(0.05);

        animations.push(tl);
    });
}

function drawResult() {
    const tl = gsap.timeline({ defaults: { ease: "power4.out", duration: 0.5 } });
    const cards = document.querySelectorAll<HTMLDivElement>(".promo-content__card");
    const cardWidth = cards[0]?.clientWidth || 0;
    const CARD_OFFSET = isDesktop ? 1.6 : 1.3;

    tl.to(
        ".promo-content__card-shadow",
        {
            x: `-=${cardWidth / CARD_OFFSET}`,
            zIndex: 9,
            duration: 0.2,
        },
        "<"
    );

    ticketCards.value.forEach((_, index) => {
        tl.to(
            `#promo-content__card--index-${index}`,
            {
                x: `-=${cardWidth / CARD_OFFSET}`,
                zIndex: index === 0 ? 10 : 1,
                duration: 0.2,
            },
            "<"
        );
    });

    tl.to(
        ".promo-content__gradient--left",
        {
            width: `-=${cardWidth}`,
            opacity: 0,
        },
        "<"
    );
    tl.to(
        ".promo-content__gradient--right",
        {
            width: `+=${cardWidth}`,
            opacity: 0,
        },
        "<"
    );
    tl.from(
        ".promo-content__gradient--radial",
        {
            opacity: 0,
        },
        "<"
    );

    tl.set(".promo-content__button-buy", {
        x: `+=${cardWidth / CARD_OFFSET}`,
    });
    tl.set(".promo-content__button-reshuffle", {
        x: `+=${cardWidth / CARD_OFFSET}`,
    });

    tl.from(".promo-content__button-buy", {
        opacity: 0,
    });

    tl.from(".promo-content__button-reshuffle", {
        opacity: 0,
    });

    animations.push(tl);
}

function killAnimations() {
    animations.forEach((animation) => {
        animation.revert();
    });
    animations = [];

    ticketCards.value.forEach((_, index) => {
        // Reset position and scale
        gsap.set(`#promo-content__card--index-${index}`, { x: 0, zIndex: 0, scale: 1 });
    });
}

function animate() {
    killAnimations();

    switch (currentAnimationState.value) {
        case "default":
            ctx = gsap.context(() => {
                defaultAnimation();
            });
            break;
        case "draw":
            ticketCards.value = selectAndShuffleCards(allTicketCards);

            ctx = gsap.context(() => {
                nextTick(() => {
                    drawAnimation();
                });
            });
            break;
        case "drawResult":
            ctx = gsap.context(() => {
                nextTick(() => {
                    drawResult();
                });
            });
            break;
    }
}

watch(currentAnimationState, () => {
    nextTick(() => {
        animate();
    });
});

const resetWidget = () => {
    nextTick(() => {
        if (isWidgetInMobileLayout.value) {
            appendWidgetToPage(widget, cardList);
        } else {
            appendWidgetToPage(widget, widgetsGrid);
        }
    });
};

const handleResizeAndPlacement = debounce(() => {
    const windowWidthChanged = window.innerWidth !== lastWindowWidth;
    const layoutChanged = wasWidgetInMobileLayout !== isWidgetInMobileLayout.value;

    lastWindowWidth = window.innerWidth;
    wasWidgetInMobileLayout = isWidgetInMobileLayout.value;

    if (windowWidthChanged && !layoutChanged) {
        animate();
        return;
    }

    currentAnimationState.value = "default";
    ticketCards.value = fillCardsArray(allTicketCards);
    resetWidget();
}, 200);

watch(
    checkedFilters,
    () => {
        resetWidget();
    },
    { deep: true }
);

watch(selectedSorting, () => {
    resetWidget();
});

function fillCardsArray(cards: TicketPromoCardModel[]): TicketPromoCardModel[] {
    const timesToDuplicate = Math.ceil(13 / cards.length);

    let duplicatedCards = [];
    for (let i = 0; i < timesToDuplicate; i++) {
        duplicatedCards = duplicatedCards.concat([...cards]);
    }

    return duplicatedCards;
}

function appendWidgetToPage(targetElement: HTMLElement, container: HTMLElement) {
    targetElement.remove();

    if (!isMobile.value) {
        targetElement.style.gridColumn = "unset";
        container.appendChild(targetElement);
        return;
    }

    targetElement.style.gridColumn = "1 / -1";

    if (checkedFilters.value.length > 0) {
        container.appendChild(targetElement);
        return;
    }

    const style = window.getComputedStyle(container);
    const gridTemplateColumns = style.getPropertyValue("grid-template-columns").split(" ").length;

    const targetIndex = (WIDGET_TARGET_ROW_PLACEMENT_MOBILE - 1) * gridTemplateColumns;

    const allChildren = Array.from(container.children).filter((card) => {
        // Filter out authenticated "invisible" cards
        return window.getComputedStyle(card).display !== "none";
    });

    if (allChildren.length > targetIndex) {
        // One is deducted to account for the two-column first element
        container.insertBefore(targetElement, allChildren[targetIndex - 1]);
    } else {
        container.appendChild(targetElement);
    }
}

// LIFECYCLE HOOKS
onMounted(() => {
    if (isWidgetInMobileLayout.value) {
        appendWidgetToPage(widget, cardList);
    }
    widget.style.display = "block";

    allTicketCards = (
        window as unknown as Window & {
            ticketsForRecommendation: TicketPromoCardModel[];
        }
    ).ticketsForRecommendation;

    allTicketCards = allTicketCards.filter((ticket) => !ticket.hideForNotLoggedUsers);
    ticketCards.value = fillCardsArray(allTicketCards);

    nextTick(() => {
        animate();
    });

    window.addEventListener("resize", handleResizeAndPlacement);

    wasWidgetInMobileLayout = isWidgetInMobileLayout.value;
});

onUnmounted(() => {
    // Cleans up the GSAP context to prevent memory leaks
    ctx.revert();

    window.removeEventListener("resize", handleResizeAndPlacement);
});
</script>
